import { useState } from "react";
import { Check, Copy, FileJson } from "lucide-react";
import type { SiteScan } from "../../../lib/siteScan";
import { IconBtn, Modal, PillTabs } from "../../ui";
import type { T } from "./types";

/**
 * The raw material behind a scan: response headers, the page head, and every
 * probe body.
 *
 * The panels above are an interpretation, and an interpretation on a sales call
 * has to be checkable. This is where "they run WordPress 5.4" turns into the
 * exact generator tag that said so.
 */

function RawPane({ text, empty, copyLabel }: { text: string | null; empty: string; copyLabel: string }) {
  const [copied, setCopied] = useState(false);
  if (!text) {
    return <div className="crm-raw-empty">{empty}</div>;
  }
  return (
    <div className="relative">
      <IconBtn
        small
        className="absolute right-2.5 top-2.5 z-10"
        aria-label={copyLabel}
        title={copyLabel}
        onClick={() => {
          void navigator.clipboard?.writeText(text);
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        }}
      >
        {copied ? <Check className="size-3.5" style={{ color: "var(--color-success)" }} /> : <Copy className="size-3.5" />}
      </IconBtn>
      <pre className="crm-raw-pre">{text}</pre>
    </div>
  );
}

type RawTab = "headers" | "head" | "probes";

export function ScanRawDialog({
  scan,
  open,
  onOpenChange,
  t,
}: {
  scan: SiteScan;
  open: boolean;
  onOpenChange: (v: boolean) => void;
  t: T;
}) {
  const headers = Object.entries(scan.raw.headers ?? {})
    .map(([k, v]) => `${k}: ${v}`)
    .join("\n");
  const probeKeys = Object.keys(scan.raw.probes ?? {});
  const probeBodies = probeKeys.length
    ? probeKeys.map((k) => `----- ${k} -----\n${scan.raw.probes[k]}`).join("\n\n")
    : null;
  // The exposure check shares this tab: it is the same act (ask for a path, see
  // what comes back) on a different list, and the raw view is where a finding
  // gets verified. Both the open and the closed paths are printed, because "we
  // asked and it was shut" is the half that makes the clean verdict mean
  // anything. Paths whose content is a secret carry no sample by design.
  const exposure = scan.raw.exposure;
  const exposureText = exposure?.checked?.length
    ? `----- exposure -----\n${exposure.checked
        .map((path) => {
          const hit = exposure.found.find((f) => f.path === path);
          if (!hit) return `${path}  closed`;
          return `${path}  OPEN  ${hit.bytes} B${hit.sample ? `  ${hit.sample}` : ""}`;
        })
        .join("\n")}`
    : null;
  // The malware check shares the same tab for the same reason, but prints
  // THREE states per reading rather than two. "asked and could not read" is
  // its own answer: a bot wall or a timeout is not a closed door, and folding
  // it into "closed" is exactly how a clean verdict gets claimed for a page
  // nobody saw. Samples are labels, never a byte of the code they describe.
  const malware = scan.raw.malware;
  const malwareText = malware?.checked?.length
    ? `----- malware -----\n${[
        ...malware.checked.map((probe) => {
          if (malware.answered.includes(probe)) return `${probe}  read`;
          const block = malware.blocked.find((b) => b.probe === probe);
          return `${probe}  not read  ${block?.reason ?? "unknown"}`;
        }),
        `depth    ${malware.deep ? "deep" : "page only"}`,
        `verdict  ${malware.verdict}`,
        ...malware.found.map(
          (sign) => `${sign.id}  ${sign.tier}  ${sign.probes.join(",")}  ${sign.count}x  ${sign.sample}`,
        ),
      ].join("\n")}`
    : null;
  const probes = [probeBodies, exposureText, malwareText].filter(Boolean).join("\n\n") || null;
  // Silent probes belong here rather than in the scan's error line: asking a
  // site for /wp-json and getting nothing is an outcome, not a broken scan.
  const missing = scan.raw.probesMissing ?? [];

  const [tab, setTab] = useState<RawTab>("headers");
  if (!open) return null;

  const text = tab === "headers" ? headers || null : tab === "head" ? scan.raw.head : probes;
  const empty = tab === "probes" ? t("scanner.raw.noProbes") : t("scanner.raw.empty");

  return (
    // `width`, not `max-width`: `.crm-modal` sets `width: min(520px, 100%)`, so
    // a wider `max-w-` is dead code - it can only narrow the dialog. Raw headers
    // and a page head are long lines, and this is the one dialog in the CRM that
    // genuinely needs the room.
    <Modal
      title={
        <span className="flex items-center gap-2">
          <FileJson className="size-4 text-primary" />
          {t("scanner.raw.title", { domain: scan.domain })}
        </span>
      }
      label={t("scanner.raw.title", { domain: scan.domain })}
      onClose={() => onOpenChange(false)}
      className="w-[min(900px,100%)]"
    >
      <p className="crm-sub mt-1">{t("scanner.raw.subtitle")}</p>

      <div className="mt-4">
        <PillTabs
          small
          value={tab}
          onChange={setTab}
          tabs={[
            { value: "headers" as RawTab, label: t("scanner.raw.headers"), count: headers ? undefined : 0 },
            { value: "head" as RawTab, label: t("scanner.raw.head"), count: scan.raw.head ? undefined : 0 },
            { value: "probes" as RawTab, label: t("scanner.raw.probes"), count: probeKeys.length },
          ]}
        />
      </div>

      <div className="mt-3 space-y-2">
        <RawPane text={text} empty={empty} copyLabel={t("common.copy")} />
        {tab === "probes" && missing.length > 0 && (
          <p className="text-xs text-muted-foreground">
            {t("scanner.raw.missingProbes", { list: missing.join(", ") })}
          </p>
        )}
      </div>
    </Modal>
  );
}
